import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import useQuery from '../../modules/react/hooks/useQuery';
import useMutation from '../../modules/react/hooks/useMutation';
import BreadcrumbModal from '../components/BreadcrumbModal';
import { HOME_PAGE } from '../../src/shared/pageTypes';

const GET_DATA = gql`
  query BreadcrumbBuilderModalData($id: ID!, $filter: PagesFilterInput! ) {
    site: currentSite {
      id
      ...BreadcrumbModalSite
    }
    page(id: $id) {
      id
      ...BreadcrumbModalPage
    }
    homePages: pages(filter: $filter) {
      ...BreadcrumbModalHomePages
    }
  }

  ${BreadcrumbModal.fragments.site}
  ${BreadcrumbModal.fragments.page}
  ${BreadcrumbModal.fragments.homePages}
`;

const UPDATE_BREADCRUMB_TRAIL = gql`
  mutation BreadcrumbModalEdit($pageId: String!, $breadcrumbTrail: BreadcrumbTrailInput!, $page: PageInput!) {
    breadcrumbTrailUpdate(id: $pageId, breadcrumbTrail: $breadcrumbTrail) {
      breadcrumbTrail {
        id
        name
        ancestorPages {
          id
          live
          breadcrumbTrail {
            id
            name
          }
        }
        parentPage {
          id
          breadcrumbTrail {
            id
            name
            ancestorPages {
              id
              breadcrumbTrail {
                id
                name
              }
            }
          }
        }
      }
      userErrors {
        code
        message
      }
    }
    pageUpdate(id: $pageId, page: $page) {
      page {
        id
        slug
        path
        url
        breadcrumbTrail {
          id
          descendantPages {
            id
            path
            slug
            url
          }
        }
      }
      userErrors {
        code
        message
      }
    }
  }
`;

const propTypes = {
  show: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
};

const defaultProps = {};

const BreadcrumbSettingsModal = ({ show, onDismiss, id, domain }) => {
  const { data: currentBreadcrumbData } = useQuery(GET_DATA, {
    variables: { id, filter: { types: [HOME_PAGE], live: true } },
  });
  const [
    updatePageBreadcrumbTrail, { data, error, loading },
  ] = useMutation(UPDATE_BREADCRUMB_TRAIL);
  let submitErrors;

  if (data) {
    const {
      breadcrumbTrailUpdate: {
        breadcrumbTrail: updatedBreadcrumbTrail, userErrors: breadcrumbUserErrors,
      },
      pageUpdate: { page: updatedPage, userErrors: pageUserErrors },
    } = data;

    if (updatedBreadcrumbTrail && updatedPage) {
      onDismiss();
    }

    if (breadcrumbUserErrors.length > 0 || pageUserErrors.length > 0) {
      submitErrors = [...breadcrumbUserErrors, ...pageUserErrors];
    }
  }

  if (error) {
    const message = { code: 0, message: 'Could not update breadcrumb.' };

    submitErrors = submitErrors ? [...submitErrors, message] : [message];
  }

  const onSubmit = ({ pageId, breadcrumbTrail, page }) => {
    updatePageBreadcrumbTrail({ variables: { pageId, breadcrumbTrail, page } });
  };

  if (!currentBreadcrumbData) {
    return null;
  }

  const homePages = currentBreadcrumbData.homePages.edges
    .map(edge => edge.node).filter(({ live }) => live);

  return (
    <BreadcrumbModal
      show={show}
      onDismiss={onDismiss}
      page={currentBreadcrumbData.page}
      site={currentBreadcrumbData.site}
      homePages={homePages}
      onSave={onSubmit}
      loading={loading}
      submitErrors={submitErrors}
      domain={domain}
    />
  );
};

BreadcrumbSettingsModal.propTypes = propTypes;
BreadcrumbSettingsModal.defaultProps = defaultProps;

export default BreadcrumbSettingsModal;
